import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {
    Spinner,
    SpinnerTypeOptions,
    SpinnerSizes,
    SpinnerColors,
    SpinnerBackgroundColors
} from '@techsee/techsee-ui-common/lib/spinner';
import {IPollingBirthCertificateReportController} from './controller';

import './style.scss';
import {Alert, Overlay, OverlayTrigger, Popover, Tooltip} from 'react-bootstrap';
import {Icon} from '@techsee/techsee-ui-common/lib/icons/simple-icon';
import {IWifiAnalyzerResults, ReportType} from '../../../services/AngularServices/WifiAnalyzerService';
import {GuruReportsTypes} from '@techsee/techsee-common/lib/constants/guru-reports.constants';

export interface IReportConfig {
    iconName: string;
    popupTitle: string;
    generateTooltip: string;
    viewTooltip: string;
    errorMessage?: string;
    notReadyMessage?: string;
}

export interface IPollingBirthCertificateReportProps {
    ctrl: IPollingBirthCertificateReportController;
    session: IWifiAnalyzerResults;
    reportType: ReportType;
    reportConfig: IReportConfig;
    onForwardReport?: (id: string, reportType: ReportType) => void;
    disabled?: boolean;
}

@observer
export class PollingBirthCertificateReport extends TechseeBaseComponent<IPollingBirthCertificateReportProps> {
    state = {
        isLoading: false,
        showToast: false,
        showPopup: false,
        errorMessage: ''
    };

    private iconRef: HTMLButtonElement | null = null;

    private isAggregated(): boolean {
        return this.props.reportType === GuruReportsTypes.AGGREGATED;
    }

    componentDidMount() {
        const {ctrl, session, reportType} = this.props;

        if (this.isAggregated()) {
            if (session?.aggregatedBirthCertificateReportUrl) {
                ctrl.setReportUrl(session.aggregatedBirthCertificateReportUrl);
            }
        } else {
            if (session && session._id) {
                ctrl.setReport(session._id, reportType);
            }
        }
    }

    componentDidUpdate(prevProps: IPollingBirthCertificateReportProps) {
        const {ctrl, session, reportType} = this.props;

        if (this.isAggregated()) {
            // Only update if URL actually changed
            if (
                session?.aggregatedBirthCertificateReportUrl &&
                prevProps.session?.aggregatedBirthCertificateReportUrl !== session.aggregatedBirthCertificateReportUrl
            ) {
                ctrl.setReportUrl(session.aggregatedBirthCertificateReportUrl);
            }
        } else {
            // For customer/technician reports
            if (
                session &&
                session._id &&
                (prevProps.session?._id !== session._id || prevProps.reportType !== reportType)
            ) {
                ctrl.setReport(session._id, reportType);
            }
        }
    }

    componentWillUnmount() {
        this.setState({isLoading: false, showToast: false, showPopup: false, errorMessage: ''});
    }

    handleGenerateReport = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const {ctrl, session, reportType, reportConfig} = this.props;
        const {reportUrl, generateReport} = ctrl;

        // For aggregated reports, URL is already set from session data
        if (this.isAggregated()) {
            if (reportUrl) {
                this.setState({showPopup: true});
            }
            return;
        }

        // For customer/technician reports
        if (session && session._id) {
            ctrl.setReport(session._id, reportType);
        } else {
            this.setState({
                showToast: true,
                errorMessage: reportConfig.errorMessage
            });
            return;
        }

        if (reportUrl) {
            this.setState({showPopup: true});
            return;
        }

        this.setState({isLoading: true, showToast: false, errorMessage: ''});

        generateReport()
            .then((result) => {
                this.setState({isLoading: false});
                if (result.success && result.url) {
                    this.setState({showPopup: true});
                }
            })
            .catch(() => {
                this.setState({
                    isLoading: false,
                    showToast: true,
                    errorMessage: reportConfig.errorMessage
                });

                setTimeout(() => {
                    this.setState({showToast: false});
                }, 10000);
            });
    };

    handlePopupClose = () => {
        this.setState({showPopup: false});
    };

    handleToastClose = () => {
        this.setState({showToast: false});
    };

    handleOpenReport = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const {ctrl, session, reportType} = this.props;

        // For non-aggregated reports, set the report context
        if (!this.isAggregated() && session && session._id) {
            ctrl.setReport(session._id, reportType);
        }

        if (ctrl.reportUrl) {
            window.open(ctrl.reportUrl, '_blank');
        }

        this.setState({showPopup: false});
    };

    handleForwardReport = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const {session, reportType, onForwardReport} = this.props;

        this.setState({showPopup: false});

        const id = this.isAggregated() ? session.roomId : session._id;

        if (onForwardReport && id) {
            onForwardReport(id, reportType);
        }
    };

    renderToast() {
        const {showToast, errorMessage} = this.state;
        if (!showToast) return null;

        return (
            <div className='toast-container'>
                <Alert bsStyle='danger' onDismiss={this.handleToastClose} className='bootstrap-toast'>
                    <i className='glyphicon glyphicon-exclamation-sign' style={{marginRight: '8px'}} />
                    {errorMessage}
                </Alert>
            </div>
        );
    }

    renderPopup() {
        const {translate} = this.props.ctrl;
        const {reportConfig} = this.props;
        const {showPopup} = this.state;

        return (
            <Overlay
                show={showPopup}
                target={this.iconRef as HTMLElement}
                placement='top'
                containerPadding={10}
                rootClose
                onHide={this.handlePopupClose}>
                <Popover id='wifi-analyzer-history-report-popup' className='wifi-analyzer-history-report-popup'>
                    <div className='popover-content'>
                        <div className='popup-text'>{reportConfig.popupTitle}</div>
                        <div className='popup-icons'>
                            <div
                                className='icon-wrapper'
                                title={translate('REACT.WIFI_ANALYZER.FORWARD_REPORT')}
                                onClick={this.handleForwardReport}>
                                <Icon iconName='share' />
                            </div>
                            <div
                                className='icon-wrapper'
                                title={reportConfig.viewTooltip}
                                onClick={this.handleOpenReport}>
                                <Icon iconName='visibility-outlined' />
                            </div>
                        </div>
                    </div>
                </Popover>
            </Overlay>
        );
    }

    renderInternal(): JSX.Element {
        const {translate, reportUrl} = this.props.ctrl;
        const {isLoading} = this.state;
        const {reportType, reportConfig, disabled} = this.props;

        const tooltipText = this.isAggregated()
            ? reportConfig.generateTooltip
            : reportUrl
              ? translate('REACT.WIFI_ANALYZER.VIEW_REPORT_OPTIONS')
              : reportConfig.generateTooltip;

        return (
            <>
                {this.renderToast()}

                <OverlayTrigger
                    placement='top'
                    overlay={
                        <Tooltip id={`tooltip-${reportType}`} className='tooltip-wifi-history'>
                            {tooltipText}
                        </Tooltip>
                    }
                    delayShow={200}
                    delayHide={0}>
                    <button
                        type='button'
                        className={'unstyled-icon-button action-icon'}
                        onClick={isLoading ? undefined : this.handleGenerateReport}
                        ref={(ref) => (this.iconRef = ref)}
                        disabled={disabled || isLoading}>
                        {isLoading ? (
                            <Spinner
                                spinnerType={SpinnerTypeOptions.CIRCULAR}
                                sizeName={SpinnerSizes.XXSmall}
                                backgroundColorName={SpinnerBackgroundColors.Transparent}
                                colorName={SpinnerColors.LightBlue}
                            />
                        ) : (
                            <Icon iconName={reportConfig.iconName} />
                        )}
                    </button>
                </OverlayTrigger>
                {this.renderPopup()}
            </>
        );
    }
}
